import { isRowLoaded, Row, useInfiniteScrollTable } from "@flaminem/ui-components";
import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { DimensionedLayout } from "src/components/DimensionedLayout/DimensionedLayout.tsx";
import { useDimensions } from "src/components/DimensionedLayout/useDimensions.ts";
import { ModalTransition } from "src/components/Modal";
import { InfiniteScrollTable } from "src/components/Tables/InfiniteScrollTable.tsx";
import { defaultRowHeight } from "src/config/table-config.ts";
import { useDynamicViewSchema } from "src/dynamic-view-renderer/DynamicViewSchemaProvider/DynamicViewSchemaProvider";
import { ExecuteScenarioModal } from "src/features/scenario/ExecuteScenario";
import { useSearchCasesQuery } from "src/graphql/anti-fraud/queries/cases/SearchCasesQuery.ts";
import { useSearch } from "src/hooks/useSearch.ts";
import { useT } from "src/internationalization";
import { CasesToolbar } from "src/pages/Cases/CasesToolbar";
import { ExecuteRuleModal } from "src/pages/Cases/execute-rule/ExecuteRuleModal";
import { useCasesTableURLParams } from "src/pages/Cases/useCasesTableURLParams.ts";
import { CaseSummary } from "src/types/case";

import { useGetColumns } from "./useGetColumns";

const RequestsTab: React.FC = () => {
  const navigate = useNavigate();
  const t = useT();
  const params = useParams<{ code: string }>();

  const {
    offset,
    searchTerm,
    index,
    handleOnSearchTermChange,
    handleOnOffsetChange,
    handleOnFiltersChange,
    onlyScored,
  } = useCasesTableURLParams();
  const { pageSize, dimensions } = useDimensions();
  const { searchLoading, filteredData, searchError, refetch } = useSearch({
    searchQueryHook: useSearchCasesQuery,
    searchParams: {
      limit: pageSize,
      offset: offset,
      searchTerm: searchTerm,
      onlyScored: onlyScored,
      caseTypeCodes: params.code ? [params.code] : [],
    },
  });
  const { data, totalCount } = useInfiniteScrollTable<CaseSummary>(
    filteredData?.cases ?? [],
    filteredData?.pageInfo?.totalCount ?? 0,
    filteredData?.pageInfo?.limit ?? pageSize,
    filteredData?.pageInfo?.offset ?? offset
  );

  const [selectedCase, setSelectedCase] = useState<CaseSummary | undefined>();
  const [selectedCaseIndex, setSelectedCaseIndex] = useState<number>(0);
  const [isExecuteRuleOpen, setIsExecuteRuleOpen] = useState<boolean>(false);
  const [executeRuleCase, setExecuteRuleCase] = useState<CaseSummary | undefined>();

  const selectedRows = useMemo(() => {
    if (index != undefined) {
      const item = data[index];
      if (item) {
        return [item];
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, totalCount]);

  const handleLoadMore = async (startIndex: number, stopIndex: number) => {
    if (!searchLoading) {
      refetch({
        limit: stopIndex - startIndex + 1,
        offset: startIndex,
        searchString: searchTerm && searchTerm?.length > 0 ? searchTerm : null,
      });
    }
  };

  const handleOnRowClick = (id: string, index: number, caseTypeCode: string) => {
    handleOnOffsetChange(index < pageSize ? 0 : index - 2, index);
    navigate(`/case/${caseTypeCode}/${id}`);
  };
  const { caseTypeDefinitions, headlessWidgets } = useDynamicViewSchema();
  const caseTypeConfig = params.code
    ? caseTypeDefinitions?.find((ct) => ct.code === params.code)
    : undefined;

  const caseListTable = caseTypeConfig?.caseListTable;

  const columns = useGetColumns({
    handleOnRowClick,
    setSelectedCase,
    setSelectedCaseIndex,
    caseListTable,
    onOpenExecuteRule: (caseItem) => {
      setExecuteRuleCase(caseItem);
      setIsExecuteRuleOpen(true);
    },
    caseTypeDefinitions,
    headlessWidgets,
  });

  return (
    <>
      <DimensionedLayout>
        {dimensions?.width && (
          <InfiniteScrollTable
            id="requests-table"
            title={t("page.cases.tab.requests.table.title")}
            description={t("page.cases.tab.requests.table.description")}
            className={"flex-1"}
            totalCount={totalCount}
            rowHeight={defaultRowHeight}
            fetchMore={handleLoadMore}
            scrollToIndex={totalCount && offset ? offset : -1}
            toolbar={() => (
              <CasesToolbar
                handleOnFiltersChange={handleOnFiltersChange}
                onlyScored={onlyScored}
                searchTerm={searchTerm}
                handleOnSearchTermChange={handleOnSearchTermChange}
                caseTypeCode={params.code ? params.code : ""}
                placeholder={caseListTable?.searchPlaceholder}
              />
            )}
            data={data ?? []}
            isLoading={searchLoading}
            columns={columns}
            pageSize={pageSize}
            errorText={searchError?.message}
            onRowClick={(item: Row<CaseSummary>, rowIndex) => {
              if (isRowLoaded(item)) {
                handleOnRowClick(item.data.id, rowIndex, item.data.typeCode);
              }
            }}
            isError={searchError != null}
            highlightedRows={selectedRows}
          />
        )}
      </DimensionedLayout>
      <ModalTransition>
        {!!selectedCase && (
          <ExecuteScenarioModal
            open={!!selectedCase}
            selectedCase={selectedCase}
            showLink={true}
            onClose={() => setSelectedCase(undefined)}
            beforeSubmit={() =>
              handleOnOffsetChange(
                selectedCaseIndex < pageSize ? 0 : selectedCaseIndex - 2,
                selectedCaseIndex
              )
            }
          />
        )}
        {isExecuteRuleOpen && (
          <ExecuteRuleModal
            caseTypeCode={executeRuleCase?.typeCode ?? params.code ?? ""}
            selectedCase={executeRuleCase}
            onClose={() => {
              setIsExecuteRuleOpen(false);
              setExecuteRuleCase(undefined);
            }}
          />
        )}
      </ModalTransition>
    </>
  );
};

export default RequestsTab;
